/**
 * Everything below the Music DNA analysis: the three creative directions, the
 * editable prompt, the inspiration-strength slider, the two actions, and the
 * "Smart inspiration" reassurance card.
 *
 * The directions are a radiogroup of buttons rather than real radios — that is the
 * prototype's markup, kept because the cards carry their own selected styling. The
 * ARIA roles were added in Phase 1 so the group is still announced correctly.
 *
 * Both actions wait behind the app's shared processing state, but neither wave is
 * rendered here: the route swaps this whole block out for one while a reroll runs, and
 * puts the other underneath it during generation. This component only reports the two
 * in-flight flags on its buttons. See routes/MusicDNA.tsx.
 */
import { CheckCircle2, Music2, RefreshCw, ShieldCheck, Sliders } from 'lucide-react';
import { Btn } from '../ui';
import { strengthLabel } from '../../lib/music';
import { c } from '../../styles/theme';
import type { DnaDirection } from '../../types/domain';
import SelectedBonusPacks from '../bonus/SelectedBonusPacks';

/** The four reassurance lines under "Smart inspiration". */
const GUARANTEES = [
  'Uses inspiration only',
  'Never copies songs',
  'Creates original compositions',
  'Commercial usage ready',
];

export interface DnaPromptComposerProps {
  directions: DnaDirection[];
  /** Index of the chosen direction. */
  selectedDirection: number;
  onSelectDirection: (index: number) => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  /** 0–100. */
  inspirationStrength: number;
  onStrengthChange: (value: number) => void;
  onRegenerate: () => void;
  onGenerate: () => void;
  /**
   * Disables Regenerate while a reroll's processing state is showing.
   *
   * It had no in-flight state at all before, because the reroll was synchronous — there
   * was no window in which to press it twice. Now there is a five-second one.
   */
  regenerating: boolean;
  /** Disables the generate button while a job is in flight. */
  generating: boolean;
}

export default function DnaPromptComposer({
  directions,
  selectedDirection,
  onSelectDirection,
  prompt,
  onPromptChange,
  inspirationStrength,
  onStrengthChange,
  onRegenerate,
  onGenerate,
  regenerating,
  generating,
}: DnaPromptComposerProps) {
  return (
    <>
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>Three creative directions</p>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}
        role="radiogroup"
        aria-label="Creative direction"
      >
        {directions.map((d, i) => (
          <button
            key={d.label}
            type="button"
            role="radio"
            aria-checked={selectedDirection === i}
            onClick={() => onSelectDirection(i)}
            style={{
              textAlign: 'left',
              border:
                selectedDirection === i ? `2px solid ${c.accentBorder}` : `1px solid ${c.border}`,
              background: selectedDirection === i ? c.accentSoft : c.surface1,
              borderRadius: 10,
              padding: '10px 14px',
              cursor: 'pointer',
            }}
          >
            <p
              style={{
                fontSize: 13,
                fontWeight: 500,
                margin: 0,
                color: selectedDirection === i ? c.accent : c.text1,
              }}
            >
              {d.label}
            </p>
          </button>
        ))}
      </div>

      <label
        htmlFor="dna-prompt"
        style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 8px' }}
      >
        Original AI prompt (editable)
      </label>
      <SelectedBonusPacks />
      <textarea
        id="dna-prompt"
        rows={4}
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        style={{
          width: '100%',
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          resize: 'none',
          fontSize: 13,
          padding: 12,
          color: c.text1,
          marginBottom: 10,
          boxSizing: 'border-box',
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <label
          htmlFor="dna-strength"
          style={{
            fontSize: 12,
            color: c.text2,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Sliders size={12} />
          Inspiration strength
        </label>
        <span style={{ fontSize: 11, color: c.text2 }}>{strengthLabel(inspirationStrength)}</span>
      </div>
      <input
        id="dna-strength"
        type="range"
        min={0}
        max={100}
        value={inspirationStrength}
        onChange={(e) => onStrengthChange(Number(e.target.value))}
        style={{ width: '100%', marginBottom: 14, accentColor: c.accentFill }}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Each disables on its own flag, not on both: they are separate waits, and
            greying out Regenerate during a generation would suggest it is part of it. */}
        <Btn variant="secondary" onClick={onRegenerate} disabled={regenerating}>
          <RefreshCw size={13} />
          {regenerating ? 'Regenerating…' : 'Regenerate prompt'}
        </Btn>
        <Btn onClick={onGenerate} disabled={generating}>
          <Music2 size={13} />
          {generating ? 'Generating…' : 'One-click generate'}
        </Btn>
      </div>

      <div
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          padding: '12px 14px',
        }}
      >
        <p
          style={{
            fontSize: 12,
            fontWeight: 500,
            margin: '0 0 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ShieldCheck size={14} color={c.success} />
          Smart inspiration
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {GUARANTEES.map((line) => (
            <p
              key={line}
              style={{
                fontSize: 11,
                color: c.text2,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <CheckCircle2 size={11} color={c.success} />
              {line}
            </p>
          ))}
        </div>
      </div>
    </>
  );
}
